import { t } from "i18next";
import { Control, useController } from "react-hook-form";
import { ChangeEvent, FC, useEffect, useRef, useState } from "react";

// Styles
import { Upload } from '@carbon/icons-react';

// Components
import { Box, Skeleton } from "@mui/material";


interface ImageInputProps {
  name: string;
  control: Control<any>;
  aspectRatio?: number;
  defaultImage?: string;
  label?: string;
  gutter?: string;
  errorMessage?: string;
  hasError?: boolean;
  disabled?: boolean;
  isLoading?: boolean;
  onChange?: (value: string) => void;
}

const ImageInput: FC<ImageInputProps>  = ({
  name,
  control,
  aspectRatio = 16/9,
  defaultImage,
  label,
  gutter = '12px',
  errorMessage,
  hasError = false,
  disabled = false,
  isLoading = false,
  onChange
}) => {

  const { field } = useController({name, control});
  const uploadRef = useRef<HTMLInputElement|null>(null);
  const [inputValue, setInputValue] = useState<string|null>(null);


  useEffect(() => {
    if (field.value !== inputValue) setInputValue(field.value?.toString() || null);
  }, [field.value]);

  const onClick = () => !disabled && uploadRef?.current?.click();

  const handleImageChange = (evt?: ChangeEvent<HTMLInputElement>) => {
    if (! evt) return;
    const uploadedFile = uploadRef!.current!.files![0];
    const cachedURL = URL.createObjectURL(uploadedFile);
    setInputValue(cachedURL)
    field.onChange(cachedURL)
    field.onBlur()
    onChange?.(cachedURL)
  }

  return (
    <Box className='input__container' sx={{
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      marginBottom: gutter
      }}>

      {label && (isLoading ? <Skeleton variant="text" height="16px" width="100" style={{marginBottom: '6px'}} /> : 
        <Box display="flex" width="100%">
          <span className="label-text-02" style={{marginBottom: '0.5em', color: 'var(--text-secondary)'}}>{label}</span>
        </Box>)}

      <input 
        ref={uploadRef} 
        type="file" 
        accept="image/*" 
        hidden
        onChange={handleImageChange} 
        />

      {/* IMAGE */}
        <Box sx={{
          width: '100%',
          aspectRatio: aspectRatio,
          position: 'relative',
          cursor: disabled ? undefined : 'pointer',
          background: `url(${inputValue ?? defaultImage})`,
          backgroundPosition: 'center',
          backgroundSize: 'cover',  
          borderRadius: '3px',
          overflow: 'hidden'
        }}>

          {/* UPLOAD PROMPT */}
          <Box 
            onClick={onClick} 
            className='body-02-compact' 
            sx={{
              opacity: 0,
              transition: 'opacity 0.15s ease-in',
              position: 'absolute',
              top: 0, left: 0,
              bottom: 0, right: 0,
              color: 'white',
              background: '#00000088',
              display: 'flex', gap: 1,
              alignItems: 'center', justifyContent: 'center',
              '&:hover': {
                opacity: disabled ? 0 : 1,
              }
              }}>
              <span>{t('components.buttons.uploadImage')}</span>
              <Upload />

        </Box>
      </Box>

      {hasError && <div className="helper-text-02" style={{marginTop: '6px', color: 'var(--text-error)'}}>
        {errorMessage || t('components.imageInput.error')}
      </div>}

    </Box>
  );
};

  export default ImageInput;